import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import SidebarLinkGroup from "./SidebarLinkGroup";
import {
  LuLandPlot,
  LuLayoutDashboard,
  LuSquareActivity,
} from "react-icons/lu";
import { MdOutlineMergeType, MdPerson } from "react-icons/md";
import { GiArchitectMask } from "react-icons/gi";
import { FaBridgeCircleCheck } from "react-icons/fa6";
import {
  RiAlignItemHorizontalCenterFill,
  RiQuoteText,
  RiUserFill,
} from "react-icons/ri";
import { TbAirConditioningDisabled } from "react-icons/tb";
import { PiPersonFill } from "react-icons/pi";
import { AiOutlineTransaction } from "react-icons/ai";
import { FaQuestionCircle, FaSitemap, FaWindowRestore } from "react-icons/fa";
import { GrManual } from "react-icons/gr";
import { SiMaterialformkdocs } from "react-icons/si";
import { IoIosArrowDown, IoMdSettings } from "react-icons/io";
import logo from "../../images/logo/we.png";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
}) => {
  const { pathname } = useLocation();
  const sidebarRef = useRef(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded !== null ? storedSidebarExpanded === "true" : false
  );
  const [allowedMenuIDs, setAllowedMenuIDs] = useState([]);
  const [openGroupIndex, setOpenGroupIndex] = useState(null);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const permissionData = localStorage.getItem("userMenuPermissions");

    if (userId === "1" || permissionData === "ALL") {
      setAllowedMenuIDs("ALL");
    } else {
      const parsed = JSON.parse(permissionData || "[]");
      const ids = parsed
        .filter((p) => p.isView || p.isInsert || p.isUpdate || p.isDelete)
        .map((p) => p.menuId.toString());
      setAllowedMenuIDs(ids);
    }
  }, []);

  const MENU_GROUPS = [
    {
      title: "Dashboard",
      titleIcon: <LuLayoutDashboard className="w-[20px] h-[20px]" />,
      basePath: "/dashboard",
      items: [
        {
          label: "Dashboard",
          path: "/dashboard",
          icon: <LuLayoutDashboard />,
          menuId: "15",
        },
      ],
    },
    {
      title: "Masters",
      titleIcon: <FaSitemap className="w-[20px] h-[20px]" />,
      basePath: "/master",
      items: [
        {
          label: "Owner",
          path: "/master/owner",
          icon: <MdPerson />,
          menuId: "1",
        },
        {
          label: "Architect",
          path: "/master/architect",
          icon: <GiArchitectMask />,
          menuId: "2",
        },
        {
          label: "Fabricator",
          path: "/master/fabricator",
          icon: <FaBridgeCircleCheck />,
          menuId: "3",
        },
        {
          label: "Item",
          path: "/master/item",
          icon: <RiAlignItemHorizontalCenterFill />,
          menuId: "4",
        },
        {
          label: "Terms",
          path: "/master/terms",
          icon: <TbAirConditioningDisabled />,
          menuId: "5",
        },
        {
          label: "Customer",
          path: "/master/customer",
          icon: <PiPersonFill />,
          menuId: "6",
        },
        {
          label: "Coating-Type",
          path: "/master/coatingType",
          icon: <MdOutlineMergeType />,
          menuId: "16",
        },
        {
          label: "Fins-Item",
          path: "/master/fins-item",
          icon: <FaSitemap />,
          menuId: "17",
        },
      ],
    },
    {
      title: "Transaction",
      titleIcon: <AiOutlineTransaction className="w-[20px] h-[20px]" />,
      basePath: "/transaction",
      items: [
        {
          label: "Site-Details",
          path: "/transaction/sites",
          icon: <LuLandPlot />,
          menuId: "7",
        },
      ],
    },
    {
      title: "Quotes",
      titleIcon: <RiQuoteText className="w-[20px] h-[20px]" />,
      basePath: "/quotes",
      items: [
        {
          label: "Window-Quotation",
          path: "/quotes/window-quotation",
          icon: <FaWindowRestore />,
          menuId: "8",
        },
        {
          label: "Manual-Quotation",
          path: "/quotes/manual-quotation",
          icon: <GrManual />,
          menuId: "9",
        },
        {
          label: "Material-Quotation",
          path: "/quotes/material-quotation",
          icon: <SiMaterialformkdocs />,
          menuId: "10",
        },
        {
          label: "Fins-Quotation",
          path: "/quotes/fins-quotation",
          icon: <RiQuoteText />,
          menuId: "18",
        },
      ],
    },
    {
      title: "Settings",
      titleIcon: <IoMdSettings className="w-[20px] h-[20px]" />,
      basePath: "/settings",
      items: [
        {
          label: "Users",
          path: "/settings/user-master",
          icon: <RiUserFill />,
          menuId: "11",
        },
        {
          label: "Menu-Permission",
          path: "/settings/menu-permission",
          icon: <LuSquareActivity />,
          menuId: "12",
        },
        {
          label: "Activity-Log",
          path: "/settings/activitylog",
          icon: <LuSquareActivity />,
          menuId: "13",
        },
      ],
    },
    {
      title: "Reports",
      titleIcon: <LuLandPlot className="w-[20px] h-[20px]" />,
      basePath: "/reports",
      items: [
        {
          label: "Reports",
          path: "/reports",
          icon: <LuLandPlot />,
          menuId: "14",
        },
      ],
    },
  ];

  const filteredMenuGroups = MENU_GROUPS.map((group) => {
    const filteredItems =
      allowedMenuIDs === "ALL"
        ? group.items
        : group.items.filter((item) => allowedMenuIDs.includes(item.menuId));
    return filteredItems.length ? { ...group, items: filteredItems } : null;
  }).filter(Boolean);

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
      setOpenGroupIndex(null);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    if (sidebarExpanded) document.body.classList.add("sidebar-expanded");
    else document.body.classList.remove("sidebar-expanded");
  }, [sidebarExpanded]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setSidebarOpen(false);
        setOpenGroupIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setSidebarOpen]);

  return (
    <aside
      ref={sidebarRef}
      className={`absolute left-0 top-0 z-9999 flex h-screen ${sidebarCollapsed ? "w-20" : "w-64"
        } flex-col bg-slate-900 border-r border-slate-800 duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } shadow-2xl`}
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-between gap-2 px-6 py-7 relative border-b border-white/5 bg-slate-900/50 backdrop-blur-md">
        <NavLink to="/" className="flex items-center gap-3.5 group/logo">
          {sidebarCollapsed ? (
            <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-xl border border-white/10 group-hover/logo:scale-105 group-hover/logo:bg-white/15 transition-all duration-500 shadow-xl shadow-black/20">
              <img src={logo} alt="Logo" className="w-6 h-6 object-contain opacity-90 brightness-0 invert" />
            </div>
          ) : (
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center shadow-lg shadow-primary/20 border border-white/10">
                <img src={logo} alt="Logo" className="w-6 h-6 object-contain brightness-0 invert" />
              </div>
              <div className="flex flex-col">
                <h1 className="font-black text-base text-white tracking-widest leading-none uppercase bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/40">
                  H-Line CRM
                </h1>
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] mt-1 opacity-70">Management System</span>
              </div>
            </div>
          )}
        </NavLink>
      </div>

      {/* Navigation */}
      <div className={`flex-1 flex flex-col ${sidebarCollapsed ? "items-center" : ""} overflow-visible`}>
        <nav className="py-4 px-3 w-full">
          <ul className={`flex flex-col gap-1 ${sidebarCollapsed ? "items-center" : ""}`}>
            {filteredMenuGroups.map((group, index) => {
              const isSingleItem = group.items.length === 1;
              const singleItem = group.items[0];
              const isGroupActive = group.items.some((item) => pathname.startsWith(item.path));
              const expandedOpen = openGroupIndex === index;

              if (isSingleItem) {
                return (
                  <li key={index} className="w-full relative group/item-wrapper">
                    <NavLink
                      to={singleItem.path}
                      className={({ isActive }) =>
                        `group flex items-center gap-3 rounded-xl text-[13px] font-bold tracking-wide transition-all duration-300 ${sidebarCollapsed ? "justify-center px-0 w-12 mx-auto py-2.5" : "px-4 py-2.5"
                        } ${isActive
                          ? "bg-primary text-white shadow-lg shadow-primary/20"
                          : "text-slate-400 hover:text-white hover:bg-white/5"
                        }`
                      }
                    >
                      <span className={`text-[18px] transition-colors ${isGroupActive ? "text-white" : "text-slate-400 group-hover:text-white"}`}>
                        {singleItem.icon}
                      </span>
                      {!sidebarCollapsed && <span>{singleItem.label}</span>}
                    </NavLink>

                    {sidebarCollapsed && (
                      <div className="absolute left-full ml-4 top-1/2 -translate-y-1/2 z-50 px-3 py-1.5 bg-slate-900 text-white text-[12px] font-bold rounded-lg shadow-2xl border border-slate-800 opacity-0 invisible group-hover/item-wrapper:opacity-100 group-hover/item-wrapper:visible transition-all duration-300 whitespace-nowrap pointer-events-none">
                        {singleItem.label}
                        <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-slate-900 border-l border-b border-slate-800 rotate-45" />
                      </div>
                    )}
                  </li>
                );
              }

              return (
                <div key={index} className="relative group/group-wrapper w-full">
                  <SidebarLinkGroup activeCondition={isGroupActive}>
                    {(handleClick, open) => (
                      <>
                        <button
                          className={`w-full group relative flex items-center gap-3 rounded-xl py-2.5 text-[13px] font-bold tracking-wide transition-all duration-300 ${isGroupActive ? "text-white" : "text-slate-400"} ${sidebarCollapsed ? "justify-center px-0 w-12 mx-auto" : "px-4"} hover:text-white hover:bg-white/10`}
                          onMouseEnter={() => {
                            if (sidebarCollapsed) setOpenGroupIndex(index);
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            if (sidebarCollapsed) {
                              setOpenGroupIndex((prev) => (prev === index ? null : index));
                            } else {
                              setOpenGroupIndex((prev) => (prev === index ? null : index));
                            }
                          }}
                        >
                          <span className={`text-[18px] transition-colors ${isGroupActive ? "text-white" : "text-slate-400 group-hover:text-white"}`}>
                            {group.titleIcon}
                          </span>
                          {!sidebarCollapsed && <span className="flex-1 text-left">{group.title}</span>}
                          {!sidebarCollapsed && (
                            <IoIosArrowDown
                              className={`text-[12px] opacity-50 transition-transform duration-300 ${expandedOpen ? "rotate-180" : ""}`}
                            />
                          )}
                        </button>

                        {/* Floating Popover Menu - ONLY for Collapsed State */}
                        {sidebarCollapsed && (
                          <div
                            className={`
                              absolute left-full ml-3 top-0 z-9999 w-60 bg-primary rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] 
                              transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
                              ${expandedOpen || open ? "opacity-100 visible translate-x-0" : "opacity-0 invisible -translate-x-2"}
                              group-hover/group-wrapper:opacity-100 group-hover/group-wrapper:visible group-hover/group-wrapper:translate-x-0
                            `}
                          >
                            <div className="px-6 py-4 border-b border-white/10">
                              <span className="text-sm font-black text-white uppercase tracking-widest">{group.title}</span>
                            </div>
                            <ul className="py-3 flex flex-col gap-1 max-h-[80vh] overflow-y-auto no-scrollbar">
                              {group.items.map((item, idx) => (
                                <li key={idx}>
                                  <NavLink
                                    to={item.path}
                                    className={({ isActive }) =>
                                      `flex items-center gap-4 px-6 py-2.5 text-[13px] font-bold tracking-wide transition-all duration-200 ${isActive ? "text-white bg-white/10 shadow-inner" : "text-white/70 hover:text-white hover:bg-white/5"}`
                                    }
                                  >
                                    <span className="text-[16px] opacity-60 transition-opacity">{item.icon}</span>
                                    {item.label}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                            <div className="absolute top-4 -left-1.5 w-3 h-3 bg-primary rotate-45 rounded-sm" />
                          </div>
                        )}

                        {/* Accordion List - ONLY for Expanded State */}
                        {!sidebarCollapsed && (
                          <div className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedOpen ? "max-h-[500px] opacity-100 mt-1" : "max-h-0 opacity-0"}`}>
                            <ul className="flex flex-col gap-0.5 pl-9 pr-1">
                              {group.items.map((item, idx) => (
                                <li key={idx}>
                                  <NavLink
                                    to={item.path}
                                    className={({ isActive }) =>
                                      `relative flex items-center gap-3 rounded-lg px-3 py-1.5 text-[12.5px] font-bold tracking-wide transition-all duration-200 group/item ${isActive ? "text-white" : "text-slate-500 hover:text-slate-200"}`
                                    }
                                  >
                                    <div className={`absolute left-0 w-1 h-1 rounded-full transition-all duration-300 ${pathname.startsWith(item.path) ? "bg-primary scale-100" : "bg-slate-700 scale-0 group-hover/item:scale-100"}`} />
                                    <span className="opacity-70">{item.icon}</span>
                                    {item.label}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </SidebarLinkGroup>
                </div>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Sidebar Footer/Branding */}
      {!sidebarCollapsed && (
        <div className="p-6 border-t border-slate-800">
          <div className="bg-slate-800/30 rounded-2xl p-4 border border-slate-800">
            <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest leading-tight">CRM Version</p>
            <p className="text-[10px] text-slate-600 font-bold mt-1 tracking-wider">v2.4.0 • Enterprise Edition</p>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
